import {Button, ModalPopup, SmallLoader} from '@kit';

import React, {useEffect, useState} from 'react';

import {Feature, useClassQuery, useFeaturesByTypeQuery, useSpellsClassQuery} from '../../../generated/types';
import {getApolloError} from '../../../components/utils';
import {buildUrl, useRoute, navigate, SiteRoutes} from '../../../lib/routes';
import {FeatureSeo, FeatureView} from '../../../components/feature-view';
import {classGenitive, SECTION_ROUTES} from '../seo';
import {Markdown} from '../../../components/utils/md';

import pageCss from '../../../components/common.module.css';

type Props = {
	classId: string;
};

const sectionTypes: Record<string, Array<keyof SiteRoutes>> = {
	Metamagic: ['classMetaMagics', 'classMetaMagicFeature'],
	Invocation: ['classInvocations', 'classInvocationFeature'],
	WildMagic: ['classWildMagic', 'classWildMagicFeature'],
	Maneuver: ['classManeuvers', 'classManeuverFeature'],
	ArcaneShot: ['classArcaneShots', 'classArcaneShotFeature'],
};

export const ClassSection: React.FC<Props> = ({classId}) => {
	const {data, error, loading} = useClassQuery({variables: {id: classId}, ssr: true});
	const heroClass = data?.class;

	const router = useRoute();

	const sectionTypeId = React.useMemo(
		() =>
			Object.keys(sectionTypes).find((type) => sectionTypes[type].includes(router.routeName as keyof SiteRoutes)),
		[router.routeName],
	);

	const [showingFeatureId, setShowingFeatureId] = useState<string | null>(null);
	useEffect(() => {
		setShowingFeatureId(router.query.featureId as string);
	}, [router.query]);

	const handleCloseFeature = () => {
		if (!sectionTypeId || !heroClass) return;
		const route = sectionTypes[sectionTypeId].at(0)!;
		navigate(route, {id: heroClass.id});
		setShowingFeatureId(null);
	};

	const section = heroClass?.sections.find((s: any) => s.type === sectionTypeId);
	const type = section?.type || '';

	const {
		data: featuresData,
		loading: featuresLoading,
		error: featuresError,
	} = useFeaturesByTypeQuery({variables: {type}, ssr: true});

	const features = (featuresData?.featuresByType || []) as Feature[];
	const modalFeature = features.find((feature) => feature.id === showingFeatureId);
	// на сервере showingFeatureId ещё пуст (его ставит эффект), поэтому пункт для SEO берём из адреса
	const seoFeature = features.find((feature) => feature.id === router.query.featureId);
	const sectionRoutes = SECTION_ROUTES[type];

	return (
		<>
			{showingFeatureId && heroClass?.features && (
				<ModalPopup open={true} onClose={handleCloseFeature} historyEntry={false}>
					{modalFeature && (
						<FeatureView
							feature={modalFeature}
							parentObject={{type: 'classSection', parentId: heroClass.id, supParentId: type}}
							alwaysH1
						/>
					)}
					<ul className={'modal-popup-buttons'}>
						<li>
							<Button onClick={handleCloseFeature}>Закрыть</Button>
						</li>
					</ul>
				</ModalPopup>
			)}

			{(loading || featuresLoading) && (
				<div className={pageCss.fullSizeBlock}>
					<SmallLoader />
				</div>
			)}
			{(error || featuresError) && (
				<div className={pageCss.fullSizeBlock}>{getApolloError(error || featuresError).message}</div>
			)}
			{heroClass && section && seoFeature && sectionRoutes && (
				<FeatureSeo
					feature={seoFeature}
					section="classes"
					owner={`${String(section.title).toLowerCase()} ${classGenitive(heroClass)}`}
					lead={`${section.title} класса ${heroClass.title}`}
					canonicalPath={buildUrl(sectionRoutes.list, {id: heroClass.id} as never)}
					crumbs={[
						{name: heroClass.title, path: buildUrl('class', {id: heroClass.id})},
						{name: section.title, path: buildUrl(sectionRoutes.list, {id: heroClass.id} as never)},
						{
							name: seoFeature.title,
							path: buildUrl(sectionRoutes.feature, {id: heroClass.id, featureId: seoFeature.id} as never),
						},
					]}
				/>
			)}
			{section?.description && <Markdown text={section.description} />}
			{features.map((feature) => (
				<FeatureView
					key={feature.id}
					feature={feature as Feature}
					parentObject={{type: 'classSection', parentId: classId}}
				/>
			))}
		</>
	);
};
